<template>
  <v-dialog
    v-model="shown"
    :width="800"
    :persistent="true"
    :fullscreen="fullscreen"
  >
    <v-card v-if="shown" class="c-d-flex c-flex-column" :class="fullscreen ? 'fill-height scrollable' : null">
      <v-card-title class="py-2">
        <span class="headline">{{ title }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="fullscreen = !fullscreen">
          <v-icon>{{ fullscreen ? 'fullscreen_exit' : 'fullscreen' }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <div :style="{height: !fullscreen ? '500px' : ''}" class="c-flex-grow-1">
        <div class="monaco-editor-container" :class="isDarkMode ? 'dark-border' : 'light-border'" style="width: 100%; height: 100%" :id="editorId"></div>
      </div>
      <v-divider></v-divider>
      <div class="pb-2 pt-1 c-d-flex c-align-center c-justify-space-between px-3">
        <div class="caption">
          <code>getDoc(fieldId)</code>, <code>getValue(fieldId)</code>, <code>getTaskHeader()</code>, <code>getTaskStepsAndValues()</code>
        </div>
        <div>
          <v-btn
            @click="$emit('dismiss')"
            small
            flat
            class="pa-0 mr-2"
          >
            Cancel
          </v-btn>
          <v-btn
            @click="done()"
            color="secondary"
            small
            class="pa-0"
          >
            Done
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import loader from "@monaco-editor/loader";

export default {
  name: 'AIPromptEditor',
  props: {
    value: String,
    shown: Boolean,
    title: {
      type: String,
      default: 'Edit Prompt'
    }
  },
  data() {
    return {
      modelValue: this.value || '',
      editor: null,
      fullscreen: false,
      editorId: `aiPromptEditor_${Math.random().toString(36).substr(2, 9)}`
    }
  },
  computed: {
    isDarkMode() {
      return this.$store.state.darkTheme
    }
  },
  beforeDestroy() {
    this.cleanupEditor();
  },
  watch: {
    shown(val) {
      if (val && !this.editor) {
        this.$nextTick(() => {
          this.initEditor();
        });
      }
    },
    value: {
      immediate: true,
      handler(val) {
        this.modelValue = val || '';
        if (this.editor) {
          this.editor.setValue(this.modelValue);
        }
      }
    }
  },
  mounted() {
    if (this.shown) {
      this.initEditor();
    }
  },
  methods: {
    cleanupEditor() {
      if (this.editor) {
        this.editor.dispose();
        this.editor = null;
      }
    },

    initEditor() {
      const vm = this;
      loader.init().then((monaco) => {
        const editorElement = document.getElementById(vm.editorId);
        if (!editorElement) {
          console.error('Editor element not found:', vm.editorId);
          return;
        }

        const editorOptions = {
          language: "javascript",
          minimap: { enabled: false },
          value: vm.modelValue,
          roundedSelection: false,
          scrollBeyondLastLine: false,
          readOnly: false,
          theme: vm.isDarkMode ? "vs-dark" : "vs",
          wordWrap: "on",
          automaticLayout: true
        };

        // Validation settings
        monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
          noSemanticValidation: false,
          noSyntaxValidation: false,
        });

        // Compiler options
        monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
          target: monaco.languages.typescript.ScriptTarget.ES2020,
          allowNonTsExtensions: true,
        });

        // Add extra libraries for AI prompt functions
        const libSource = `
/**
 * Get a document from a field value
 * @param {number} fieldId - The field ID containing the document
 * @returns {Promise<string>} The filename of the document
 */
declare function getDoc(fieldId: number): Promise<string>;

/**
 * Get a value from a field
 * @param {number} fieldId - The field ID
 * @returns {Promise<string>} The field value
 */
declare function getValue(fieldId: number): Promise<string>;

/**
 * Get the task header information for the current task
 * @returns {Promise<object>} The task header data
 */
declare function getTaskHeader(): Promise<object>;

/**
 * Get all steps and their field values for the current task
 * @returns {Promise<{name: string, result: Array<{fields: object}>}>} The steps and values data
 */
declare function getTaskStepsAndValues(): Promise<{name: string, result: Array<{fields: object}>}>;
`;

        const libUri = `ts:ai-prompt-lib-${vm.editorId}.d.ts`;
        const parsedUri = monaco.Uri.parse(libUri);

        // Check if model already exists, if so dispose it first
        const existingModel = monaco.editor.getModel(parsedUri);
        if (existingModel) {
          existingModel.dispose();
        }

        // Add extra lib and create model
        monaco.languages.typescript.javascriptDefaults.addExtraLib(libSource, libUri);
        monaco.editor.createModel(libSource, "typescript", parsedUri);

        vm.editor = monaco.editor.create(editorElement, editorOptions);
      });
    },

    done() {
      if (this.editor) {
        const code = this.editor.getValue();
        this.$emit('input', code);
        this.$emit('dismiss');
      } else {
        console.error("Editor is not initialized yet.");
      }
    },
  }
}
</script>

<style scoped>
.monaco-editor-container {
}

.dark-border {
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.light-border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
